import type { SurveyPage } from '../../fetchSurvey/types';
import type { SurveyAnswers } from '../../submitSurvey/types';
import type { CustomFieldValueMap } from '../../surveyLogic/types/customFieldValues';
import { buildLogicContext } from '../../surveyLogic/context/buildLogicContext';
import { getVisibleQuestions } from '../../surveyLogic/visibility';
import { simulateActivePath } from '../../surveyProgress/utils/activePath';
import { validateQuestions } from './validation';

/** Validation failure returned when one or more pages have answer errors. */
export type SurveyValidationFailure = {
  errors: Record<string, string>;
  errorPageIndex: number;
  firstErrorId: string;
};

export function validatePagesByIndices(
  pages: SurveyPage[],
  pageIndices: number[],
  answers: SurveyAnswers,
  language?: string,
  customFieldValues: CustomFieldValueMap = {}
): SurveyValidationFailure | null {
  let errors: Record<string, string> = {};
  let firstErrorId = '';
  let errorPageIndex = -1;

  for (const i of pageIndices) {
    const page = pages[i];
    if (!page) continue;

    const pageErrors = validateQuestions(page.questions, answers, language, undefined, customFieldValues);
    if (Object.keys(pageErrors).length > 0) {
      errors = { ...errors, ...pageErrors };
      if (errorPageIndex === -1) {
        errorPageIndex = i;
        firstErrorId = Object.keys(pageErrors)[0];
      }
    }
  }

  if (errorPageIndex !== -1) {
    return { errors, errorPageIndex, firstErrorId };
  }

  return null;
}

export function validateActivePathPages(
  pages: SurveyPage[],
  answers: SurveyAnswers,
  language?: string,
  customFieldValues: CustomFieldValueMap = {}
): SurveyValidationFailure | null {
  const allQuestions = pages.flatMap(page => page.questions);
  const logicContext = buildLogicContext(answers, allQuestions, customFieldValues);
  const activePath = simulateActivePath(pages, answers, allQuestions, customFieldValues);

  let errors: Record<string, string> = {};
  let firstErrorId = '';
  let errorPageIndex = -1;

  for (const pageIndex of activePath) {
    const page = pages[pageIndex];
    if (!page) continue;

    const visibleQuestions = getVisibleQuestions(page.questions, logicContext);
    const pageErrors = validateQuestions(
      visibleQuestions,
      answers,
      language,
      allQuestions,
      customFieldValues
    );
    if (Object.keys(pageErrors).length > 0) {
      errors = { ...errors, ...pageErrors };
      if (errorPageIndex === -1) {
        errorPageIndex = pageIndex;
        firstErrorId = Object.keys(pageErrors)[0];
      }
    }
  }

  if (errorPageIndex !== -1) {
    return { errors, errorPageIndex, firstErrorId };
  }

  return null;
}
